import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  isBuukdPlatformSettlementPayment,
  isPaymentReadyForPayout,
  type PayoutBatch,
  type PlatformPaymentSettlement,
} from "@/lib/platform-settlements";

export type SettlementOverview = {
  customerVolume: number;
  platformRevenue: number;
  owedToBusinesses: number;
  readyToPayNet: number;
  readyPaymentCount: number;
  paidOutNet: number;
  onHoldNet: number;
  onHoldCount: number;
};

const PAYMENT_SELECT =
  "id,business_id,currency,gross_amount,fee_amount,net_amount,business_fee_amount,customer_fee_amount,total_charged_amount,platform_booking_fee_amount,platform_revenue_amount,fee_model,paid_at,payout_eligible_at,payer_email,context,context_id,provider_reference,payout_status,status,provider_account";

const sb = supabase as typeof supabase & {
  from: (table: string) => ReturnType<typeof supabase.from>;
  rpc: (fn: string, args?: Record<string, unknown>) => ReturnType<typeof supabase.rpc>;
};

function platformSettlementPayments(rows: PlatformPaymentSettlement[]): PlatformPaymentSettlement[] {
  return rows.filter(isBuukdPlatformSettlementPayment);
}

export function usePlatformSettlements() {
  const [loading, setLoading] = useState(true);
  const [readyPayments, setReadyPayments] = useState<PlatformPaymentSettlement[]>([]);
  const [onHoldPayments, setOnHoldPayments] = useState<PlatformPaymentSettlement[]>([]);
  const [payoutHistory, setPayoutHistory] = useState<PayoutBatch[]>([]);
  const [overview, setOverview] = useState<SettlementOverview | null>(null);
  const [businesses, setBusinesses] = useState<Record<string, { id: string; name: string; payment_method?: string | null; payment_bank?: string | null; payment_account_number?: string | null; payment_mobile_money_network?: string | null; payment_mobile_money_number?: string | null; payment_connected?: boolean | null }>>({});

  const loadBusinesses = useCallback(async (businessIds: string[]) => {
    if (businessIds.length === 0) {
      setBusinesses({});
      return;
    }
    const { data } = await sb
      .from("businesses")
      .select(
        "id,name,payment_method,payment_bank,payment_account_number,payment_mobile_money_network,payment_mobile_money_number,payment_connected",
      )
      .in("id", businessIds);
    const map: typeof businesses = {};
    for (const b of data || []) {
      map[b.id] = b;
    }
    setBusinesses(map);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [eligibleRes, historyRes, paidOutRes, signupRes, reservationRes] = await Promise.all([
        sb
          .from("platform_payments")
          .select(PAYMENT_SELECT)
          .eq("status", "success")
          .eq("provider_account", "platform")
          .eq("payout_status", "eligible")
          .not("context", "eq", "platform_subscription_setup")
          .not("context", "eq", "platform_signup_fee")
          .not("context", "eq", "appointment_reservation")
          .not("context", "eq", "appointment_group_reservation")
          .not("paid_at", "is", null)
          .order("paid_at", { ascending: true }),
        sb
          .from("platform_payouts")
          .select(
            "id,business_id,currency,total_gross_amount,total_fee_amount,total_net_amount,payout_reference,payout_notes,payout_channel,processed_at,created_at,business:businesses(id,name)",
          )
          .order("processed_at", { ascending: false })
          .limit(100),
        sb
          .from("platform_payments")
          .select("net_amount,platform_revenue_amount,fee_amount,total_charged_amount,gross_amount")
          .eq("status", "success")
          .eq("provider_account", "platform")
          .eq("payout_status", "paid_out")
          .not("context", "eq", "platform_subscription_setup")
          .not("context", "eq", "platform_signup_fee"),
        sb
          .from("platform_payments")
          .select("gross_amount,platform_revenue_amount,fee_amount")
          .eq("status", "success")
          .in("context", ["platform_subscription_setup", "platform_signup_fee"]),
        sb
          .from("platform_payments")
          .select("platform_revenue_amount,fee_amount")
          .eq("status", "success")
          .in("context", ["appointment_reservation", "appointment_group_reservation"]),
      ]);

      if (eligibleRes.error) throw eligibleRes.error;
      if (historyRes.error) throw historyRes.error;
      if (reservationRes.error) throw reservationRes.error;

      const eligible = platformSettlementPayments((eligibleRes.data || []) as PlatformPaymentSettlement[]);
      const ready = eligible.filter((p) => isPaymentReadyForPayout(p));
      const onHold = eligible.filter((p) => !isPaymentReadyForPayout(p));

      setReadyPayments(ready);
      setOnHoldPayments(onHold);
      setPayoutHistory((historyRes.data || []) as PayoutBatch[]);

      const readyNet = ready.reduce((s, p) => s + Number(p.net_amount || 0), 0);
      const owedNet = eligible.reduce((s, p) => s + Number(p.net_amount || 0), 0);
      const customerVolume = eligible.reduce(
        (s, p) => s + Number(p.total_charged_amount ?? p.gross_amount ?? 0),
        0,
      );
      const bookingRevenue = eligible.reduce(
        (s, p) => s + Number(p.platform_revenue_amount ?? p.fee_amount ?? 0),
        0,
      );
      const signupRevenue = (signupRes.data || []).reduce(
        (s: number, p: { gross_amount?: number }) => s + Number(p.gross_amount || 0),
        0,
      );
      const reservationRevenue = (reservationRes.data || []).reduce(
        (s: number, p: { platform_revenue_amount?: number; fee_amount?: number }) =>
          s + Number(p.platform_revenue_amount ?? p.fee_amount ?? 0),
        0,
      );
      const paidOutNet = (paidOutRes.data || []).reduce(
        (s: number, p: { net_amount?: number }) => s + Number(p.net_amount || 0),
        0,
      );
      const holdNet = onHold.reduce((s, p) => s + Number(p.net_amount || 0), 0);

      setOverview({
        customerVolume,
        platformRevenue: bookingRevenue + reservationRevenue + signupRevenue,
        owedToBusinesses: owedNet,
        readyToPayNet: readyNet,
        readyPaymentCount: ready.length,
        paidOutNet,
        onHoldNet: holdNet,
        onHoldCount: onHold.length,
      });

      const bizIds = Array.from(
        new Set([
          ...ready.map((p) => p.business_id),
          ...onHold.map((p) => p.business_id),
          ...((historyRes.data || []) as PayoutBatch[]).map((h) => h.business_id),
        ]),
      );
      await loadBusinesses(bizIds);
    } finally {
      setLoading(false);
    }
  }, [loadBusinesses]);

  const fetchPayoutItems = useCallback(async (payoutId: string) => {
    const { data: items, error: itemsErr } = await sb
      .from("platform_payout_items")
      .select("payment_id")
      .eq("payout_id", payoutId);
    if (itemsErr) throw itemsErr;
    const ids = (items || []).map((i: { payment_id: string }) => i.payment_id);
    if (ids.length === 0) return [] as PlatformPaymentSettlement[];
    const { data: payments, error: payErr } = await sb
      .from("platform_payments")
      .select(PAYMENT_SELECT)
      .in("id", ids);
    if (payErr) throw payErr;
    return platformSettlementPayments((payments || []) as PlatformPaymentSettlement[]);
  }, []);

  const recordPayout = useCallback(
    async (args: {
      businessId: string;
      reference: string | null;
      notes: string | null;
      channel: string;
    }) => {
      const { data, error } = await sb.rpc("create_platform_payout", {
        _business_id: args.businessId,
        _payout_reference: args.reference,
        _payout_notes: args.notes,
        _payout_channel: args.channel,
      });
      if (error) throw error;
      return data as string;
    },
    [],
  );

  return {
    loading,
    readyPayments,
    onHoldPayments,
    /** @deprecated use onHoldPayments */
    awaitingPayments: onHoldPayments,
    payoutHistory,
    overview,
    businesses,
    refresh,
    fetchPayoutItems,
    recordPayout,
  };
}
